import React from "react";

/** Zodiac wheel: 12 sign ticks, inner ring, crescent moon and stars, slowly turning. */
export const ZodiacSigil: React.FC<{ size?: number }> = ({ size = 72 }) => {
  const ticks = Array.from({ length: 12 }, (_, i) => i * 30);
  return (
    <div
      className="astria-sigil relative mx-auto mb-5"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <style>{`.astria-sigil{--s1:#E9DFFF;--s2:#A78BFF;--s3:#F3D9A4;--disc:rgba(20,14,40,0.55);--star:#F3D9A4;--star2:#E9DFFF;--glow:transparent}
html[data-theme="light"] .astria-sigil{--s1:#B79CFF;--s2:#7C5CE0;--s3:#D9A24C;--disc:#F4EEFF;--star:#D9A24C;--star2:#9A7BE8;--glow:rgba(167,139,255,0.35)}
@keyframes astria-wheel{to{transform:rotate(360deg)}}@keyframes astria-twinkle{0%,100%{opacity:.35}50%{opacity:1}}`}</style>
      <div
        className="absolute inset-[-18%] rounded-full"
        style={{ background: "radial-gradient(circle, var(--glow) 0%, transparent 68%)" }}
      />
      <svg viewBox="0 0 100 100" width={size} height={size} className="relative">
        <defs>
          <linearGradient id="astria-sigil-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" style={{ stopColor: "var(--s1)" }} />
            <stop offset="50%" style={{ stopColor: "var(--s2)" }} />
            <stop offset="100%" style={{ stopColor: "var(--s3)" }} />
          </linearGradient>
        </defs>
        <g style={{ transformOrigin: "50px 50px", animation: "astria-wheel 60s linear infinite" }}>
          <circle cx="50" cy="50" r="47" fill="none" stroke="url(#astria-sigil-gold)" strokeWidth="1" />
          <circle cx="50" cy="50" r="39" fill="none" stroke="url(#astria-sigil-gold)" strokeWidth="0.6" opacity="0.7" />
          {ticks.map((deg) => (
            <g key={deg} transform={`rotate(${deg} 50 50)`}>
              <line x1="50" y1="3" x2="50" y2="11" stroke="url(#astria-sigil-gold)" strokeWidth="1" />
            </g>
          ))}
          {ticks.map((deg) => (
            <circle key={`d${deg}`} cx="50" cy="15" r="1.2" style={{ fill: "var(--star)" }} transform={`rotate(${deg + 15} 50 50)`} />
          ))}
        </g>
        <circle cx="50" cy="50" r="30" style={{ fill: "var(--disc)" }} stroke="url(#astria-sigil-gold)" strokeWidth="0.8" />
        {/* crescent moon */}
        <path
          d="M58 36a16 16 0 1 0 0 28a13 13 0 1 1 0-28z"
          fill="url(#astria-sigil-gold)"
        />
        {/* stars */}
        <path
          d="M62 42l1.6 3.4 3.4 1.6-3.4 1.6L62 52l-1.6-3.4-3.4-1.6 3.4-1.6z"
          style={{ fill: "var(--star)", animation: "astria-twinkle 3s ease-in-out infinite" }}
        />
        <circle cx="66" cy="60" r="1.1" style={{ fill: "var(--star2)", animation: "astria-twinkle 4s ease-in-out infinite .8s" }} />
        <circle cx="40" cy="30" r="0.9" style={{ fill: "var(--star2)", animation: "astria-twinkle 3.5s ease-in-out infinite 1.4s" }} />
      </svg>
    </div>
  );
};

/** One calm hero per page, generous spacing, soft glow. */
export const PageShell: React.FC<{
  title: string;
  sentence?: string;
  wide?: boolean;
  /** Drops the bottom padding so a footer can end the page. */
  flushBottom?: boolean;
  /** Shows a back button above the header when provided. */
  onBack?: () => void;
  backLabel?: string;
  children: React.ReactNode;
}> = ({ title, sentence, wide, flushBottom, onBack, backLabel = "Back", children }) => (
  <div className={`min-h-full px-4 pt-10 sm:px-8 ${flushBottom ? "pb-4 sm:pb-6" : "pb-10"}`}>
    <div className={`mx-auto space-y-8 ${wide ? "max-w-5xl" : "max-w-3xl"}`}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="-mb-2 inline-flex items-center gap-2 text-sm text-astria-muted transition hover:text-astria-text"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {backLabel}
        </button>
      )}
      <header className="text-center">
        <ZodiacSigil />
        <h1 className="text-3xl font-light tracking-tight text-astria-text">{title}</h1>
        {sentence && <p className="mt-2 text-astria-muted">{sentence}</p>}
      </header>
      {children}
    </div>
  </div>
);

export const SoftCard: React.FC<{
  children: React.ReactNode;
  premium?: boolean;
  className?: string;
}> = ({ children, premium, className = "" }) => (
  <div
    className={`p-5 text-astria-text ${className}`}
    style={{
      background: "var(--astria-surface)",
      borderRadius: premium ? "var(--astria-radius-card-premium)" : "var(--astria-radius-card)",
      boxShadow: premium
        ? "0 0 32px var(--astria-accent-glow), var(--astria-shadow-card)"
        : "var(--astria-shadow-card)",
      border: premium ? "1px solid rgba(167,139,255,0.25)" : "none",
    }}
  >
    {children}
  </div>
);

export const PrimaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className = "",
  style,
  ...rest
}) => (
  <button
    {...rest}
    className={`px-5 py-3 font-medium text-astria-bg transition hover:brightness-110 disabled:opacity-50 ${className}`}
    style={{
      background: "var(--astria-accent-primary)",
      borderRadius: "var(--astria-radius-button)",
      boxShadow: "var(--astria-shadow-button)",
      ...style,
    }}
  />
);

export const SecondaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className = "",
  style,
  ...rest
}) => (
  <button
    {...rest}
    className={`px-5 py-3 text-astria-text transition hover:bg-white/10 ${className}`}
    style={{
      background: "var(--astria-surface-soft)",
      borderRadius: "var(--astria-radius-button)",
      border: "1px solid rgba(255,255,255,0.08)",
      ...style,
    }}
  />
);

export const GhostButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className = "",
  ...rest
}) => (
  <button
    {...rest}
    className={`px-4 py-2 text-sm text-astria-accent transition hover:bg-white/10 ${className}`}
    style={{ borderRadius: "var(--astria-radius-button)" }}
  />
);
